import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "config/api";
import { useDoctorRecommendations } from "../../components/hooks/useDoctorRecommendations";
import { cachedApiCall, CACHE_KEYS } from "utils/apiCache";

/**
 * Custom hook for managing all patient data state
 * Centralizes state management for the PatientDetails component
 * Handles API data fetching, form state, and data synchronization
 */
export const usePatientData = (patient, doctor_id = null) => {
  // Use the doctor recommendations hook for API-based suggestions
  const {
    recommendations: apiRecommendations,
    isLoading: isLoadingRecommendations,
    error: recommendationsError,
    getSeverityOptions,
    getComplaintSuggestions,
    getSeverityForComplaint
  } = useDoctorRecommendations(doctor_id);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPastVisitsOpen, setIsPastVisitsOpen] = useState(false);
  const [isEndConsultationModalOpen, setIsEndConsultationModalOpen] = useState(false);

  // Patient and visit data
  const [patientData, setPatient] = useState({});
  const [hasPastVisits, setHasPastVisits] = useState(false);
  const [forPastVisitIdOfPatient, setForPastVisitIdOfPatient] = useState("");
  const [parameters, setparameters] = useState(null);

  // Consultation type and payment
  const [consultationType, setConsultationType] = useState({
    firstConsultation: false,
    followUp: false,
  });
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");

  // Speech to text toggle
  const [isSpeechToTextEnabled, setIsSpeechToTextEnabled] = useState(false);

  // Medical data states - each medicine component maintains its own separate state
  const [medicines, setMedicines] = useState([]);
  const [food, setFood] = useState([]);
  const [other, setOther] = useState([]);
  const [complaints, setComplaints] = useState([
  ]);
  const [diagnosis, setDiagnosis] = useState([
  ]);
  const [advicesData, setAdvicesData] = useState([
  ]);
  
  // Clinical findings state
  const [clinicalFindingsData, setClinicalFindingsData] = useState([]);
  const [newClinicalFinding, setNewClinicalFinding] = useState({
    finding: ""
  });
  const [PastHistoryData, setPastHistoryData] = useState([]);
  const [newPastHistory, setNewPastHistory] = useState({
    pasthistory: "",
    severity: "",
    duration: "",
    date: ""
  });

  // Medicine management states - Complete table structure matching: No. | Medicine | Type | Dosage | Frequency | Duration | How to take | From To | Action
  const [medicinesData, setMedicinesData] = useState([]);
  const [newMedicineData, setNewMedicineData] = useState({ 
    name: "", 
    type: "", 
    dosage: "", 
    frequency: "", 
    duration: "", 
    howToTake: "",
    fromTo: "" 
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Form input states
  const [newComplaint, setNewComplaint] = useState({
    complaint: "",
    severity: "",
    duration: "",
    date: ""
  });
  const [newDiagnosis, setNewDiagnosis] = useState({ 
    diagnosis: "", 
    duration: "", 
    date: "" 
  });
  const [newMedicine, setNewMedicine] = useState({ 
    name: "", 
    dosage: "", 
    frequency: "", 
    duration: "",
    fromTo: ""
  });
  const [newFood, setNewFood] = useState("");
  const [newOther, setNewOther] = useState("");
  const [newAdvice, setNewAdvice] = useState({ 
    advice: "", 
    date: new Date().toISOString().split('T')[0] 
  });

  // A sync token that can be bumped to notify children to re-pull from parent data
  const [templateSyncToken, setTemplateSyncToken] = useState({ t: 0 });

  // Next visit and referral states
  const [nextVisitDuration, setNextVisitDuration] = useState("");
  const [nextVisitDate, setNextVisitDate] = useState("");
  const [referredDoctor, setReferredDoctor] = useState("");
  const [referredSpeciality, setReferredSpeciality] = useState("");
  const [referredPhone, setReferredPhone] = useState("");
  const [referredEmail, setReferredEmail] = useState("");
  const [referredHospital, setReferredHospital] = useState("");
  const [referredHospitalAddress, setReferredHospitalAddress] = useState("");
  const [investigation, setInvestigation] = useState("");
  const [investigationsList, setInvestigationsList] = useState([]);
  const [pastMedication, setPastMedication] = useState("");

  // Additional states
  const [additionalMedicines, setAdditionalMedicines] = useState([]);
  const [additionalFood, setAdditionalFood] = useState([]);
  const [additionalOther, setAdditionalOther] = useState([]);
  const [tests, setTests] = useState([
    { no: 1, testName: "Blood Test", byWhen: "2024-06-10" },
  ]);
  const [newTest, setNewTest] = useState({ testName: "", byWhen: "" });

  // Static data
  const [doctor] = useState({
    name: "Dr. John Smith",
    qualification: "M.B.B.S., M.D.",
    registrationNumber: "12345",
    clinicName: "City Medical Center",
    address: "123 Medical Street, City",
    phone: "123-456-7890",
    email: "dr.smith@example.com"
  });

  const [allergiesData] = useState({
    medicines: ["Penicillin", "Sulfa drugs"],
    food: ["Peanuts", "Shellfish"],
    other: ["Dust", "Pollen"]
  });

  const [followUpDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedLanguage, setSelectedLanguage] = useState("english");

  // State for doctor recommendations (keeping for backward compatibility)
  const [doctorRecommendations, setDoctorRecommendations] = useState({
    medicines: [],
    diagnosis: [],
    complaints: [],
    allergies: {
      medicines: [],
      food: [],
      other: []
    },
    advices: []
  });

  // State for prescription data passed from consultation
  const [prescriptionData, setPrescriptionData] = useState(null);

  // Update doctorRecommendations state when API recommendations change
  useEffect(() => {
    if (apiRecommendations) {
      setDoctorRecommendations(apiRecommendations);
    }
  }, [apiRecommendations]);

  // Track if we've already fetched for this appointment
  const parametersFetchedRef = useRef(null);

  // Fetch parameters and past visits when patient changes
  useEffect(() => {
    const appointmentId = patient?.appointment_id;

    // Skip if no appointment ID or already fetched for this appointment
    if (!appointmentId || parametersFetchedRef.current === appointmentId) {
      return;
    }

    localStorage.setItem('APPID_CUR', appointmentId || "NONE");
    parametersFetchedRef.current = appointmentId;

    // const fetchparameters = async () => {
    //   try {
    //     const cacheKey = `parameters-by-appointment-${appointmentId}`;
    //
    //     // Use cached API call for parameters
    //     const response = await cachedApiCall(
    //       cacheKey,
    //       async () => {
    //         console.log(`[usePatientData] Fetching parameters for appointment ${appointmentId}`);
    //         const res = await axiosInstance.get(`api/parameters/byAppointment?appointment_id=${appointmentId}`);
    //         return res.data;
    //       },
    //       { ttl: 5 * 60 * 1000 } // 5 minute cache
    //     );
    //
    //     if (response) {
    //       console.log('[usePatientData] Fetched parameters:', response);
    //       const patientId = response.patient_id;
    //       setForPastVisitIdOfPatient(patientId);
    //       setparameters(response);
    //
    //       // Then check if this patient has any past prescriptions
    //       if (patientId) {
    //         console.log('[usePatientData] Checking prescription history for patient ID:', patientId);
    //         try {
    //           const prescriptionCacheKey = CACHE_KEYS.PRESCRIPTIONS_LATEST(patientId);
    //
    //           const prescriptionData = await cachedApiCall(
    //             prescriptionCacheKey,
    //             async () => {
    //               console.log(`[usePatientData] Fetching prescription history for patient ${patientId}`);
    //               const prescriptionResponse = await axiosInstance.get(`api/prescription/getPrescriptions?patient_id=${patientId}&latest=true`);
    //               return prescriptionResponse.data;
    //             },
    //             { ttl: 2 * 60 * 1000 } // 2 minute cache
    //           );
    //
    //           const hasPrescriptions = Array.isArray(prescriptionData?.data) && prescriptionData.data.length > 0;
    //           console.log('[usePatientData] Patient has prescription history:', hasPrescriptions);
    //           setHasPastVisits(hasPrescriptions);
    //         } catch (err) {
    //           console.error('[usePatientData] Error checking prescription history:', err);
    //           // Fallback to check old endpoint if needed
    //           try {
    //             const pastVisitsResponse = await axiosInstance.get(`api/appointment/past/patient?patient_id=${patientId}`);
    //             const hasVisits = pastVisitsResponse.data?.pastAppointments?.length > 0;
    //             setHasPastVisits(hasVisits);
    //           } catch (fallbackErr) {
    //             console.error('[usePatientData] Fallback check for past visits also failed:', fallbackErr);
    //             setHasPastVisits(false);
    //           }
    //         }
    //       } else {
    //         console.log('[usePatientData] No patient ID found in parameters response');
    //         setHasPastVisits(false);
    //       }
    //     }
    //   } catch (error) {
    //     console.error('[usePatientData] Error fetching parameters:', error);
    //     setHasPastVisits(false);
    //   }
    // };

    // fetchparameters();
    
    // Cleanup function to remove the appointment ID when component unmounts
    return () => {
      localStorage.removeItem(' APPID_CUR');
    };
  }, [patient?.appointment_id]);

  // Save form data to localStorage for persistence
  useEffect(() => {
    const currentFormData = {
      // Patient info
      patient_id: patient.patient_id || patient.id,
      patient_name: patient.name,
      patient_age: patient.age,
      patient_gender: patient.gender,
      patient_phone:
        patient.phone_no ||
        patient.mobile_no ||
        patient.phone ||
        patient.phoneNumber,

      // Form data
      amount,
      paymentMode,
      consultationType,

      // Medical data
      medicines,
      food,
      other,
      complaints,
      diagnosis,
      medicinesData,
      advicesData,
      clinicalFindingsData,
      PastHistoryData,
      additionalMedicines,
      additionalFood,
      additionalOther,

      // Visit data
      nextVisitDuration,
      nextVisitDate,
      referredDoctor,
      referredSpeciality,
      referredPhone,
      referredEmail,
      referredHospital,
      referredHospitalAddress,
      investigationsList,
      pastMedication,

      // Metadata
      lastUpdated: new Date().toISOString(),
      isSpeechToTextEnabled,
    };

    // Save to localStorage
    try {
      localStorage.setItem(
        "currentPatientFormData",
        JSON.stringify(currentFormData)
      );
    } catch (error) {
      console.error("Error saving form data to localStorage:", error);
    }
  }, [
    amount,
    paymentMode,
    consultationType,
    medicines,
    food,
    other,
    complaints,
    diagnosis,
    medicinesData,
    advicesData,
    clinicalFindingsData,
    PastHistoryData,
    additionalMedicines,
    additionalFood,
    additionalOther,
    nextVisitDuration,
    nextVisitDate,
    referredDoctor,
    referredSpeciality,
    referredPhone,
    referredEmail,
    investigationsList,
    pastMedication,
    isSpeechToTextEnabled,
    referredHospital,
    referredHospitalAddress,
    patient
  ]);

  return {
    // API data
    apiRecommendations,
    isLoadingRecommendations,
    recommendationsError,
    getSeverityOptions,
    getComplaintSuggestions,
    getSeverityForComplaint,
    doctorRecommendations,

    // Modal states
    isModalOpen,
    setIsModalOpen,
    isPreviewOpen,
    setIsPreviewOpen,
    isPastVisitsOpen,
    setIsPastVisitsOpen,
    isEndConsultationModalOpen,
    setIsEndConsultationModalOpen,

    // Patient data
    patientData,
    setPatient,
    hasPastVisits,
    setHasPastVisits,
    forPastVisitIdOfPatient,
    setForPastVisitIdOfPatient,
    parameters,
    setparameters,

    // Consultation data
    consultationType,
    setConsultationType,
    amount,
    setAmount,
    paymentMode,
    setPaymentMode,
    isSpeechToTextEnabled,
    setIsSpeechToTextEnabled,

    // Medical data - each component maintains its own separate state
    medicines,
    setMedicines,
    food,
    setFood,
    other,
    setOther,
    complaints,
    setComplaints,
    diagnosis,
    setDiagnosis,
    advicesData,
    setAdvicesData,
    clinicalFindingsData,
    setClinicalFindingsData,
    // Past History 
    PastHistoryData,
    setPastHistoryData,
    // Medicine management
    medicinesData,
    setMedicinesData,
    newMedicineData,
    setNewMedicineData,
    fromDate,
    setFromDate,
    toDate,
    setToDate,

    // Form inputs
    newComplaint,
    setNewComplaint,
    newDiagnosis,
    setNewDiagnosis,
    newMedicine,
    setNewMedicine,
    newFood,
    setNewFood,
    newOther,
    setNewOther,
    newAdvice,
    setNewAdvice,
    newClinicalFinding,
    setNewClinicalFinding,
    newPastHistory,
    setNewPastHistory,

    // Next visit and referral
    nextVisitDuration,
    setNextVisitDuration,
    nextVisitDate,
    setNextVisitDate,
    referredDoctor,
    setReferredDoctor,
    referredSpeciality,
    setReferredSpeciality,
    referredPhone,
    setReferredPhone,
    referredEmail,
    setReferredEmail,
    referredHospital,
    setReferredHospital,
    referredHospitalAddress,
    setReferredHospitalAddress,
    investigation,
    setInvestigation,
    investigationsList,
    setInvestigationsList,
    pastMedication,
    setPastMedication,

    // Additional data
    additionalMedicines,
    setAdditionalMedicines,
    additionalFood,
    setAdditionalFood,
    additionalOther,
    setAdditionalOther,
    tests,
    setTests,
    newTest,
    setNewTest,

    // Static data
    doctor,
    allergiesData,
    followUpDate,
    selectedLanguage,
    setSelectedLanguage,

    // Prescription data
    prescriptionData,
    setPrescriptionData,
  templateSyncToken,
  setTemplateSyncToken,
  };
};